import { useUrlFilters } from '@/shared/hooks/useUrlFilters';
import type { ListUsersQuery } from '@skewb-works/contracts';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { UsersView } from '../components/UsersView';
import { useUsers } from '../hooks/useUsers';
import type { User } from '../types/user';
import { userListItemToView } from '../utils/mappers';

const FILTER_DEFAULTS = {
  search: '',
  tab: 'normal',
  name: '',
  email: '',
  jobTitle: 'all',
  team: 'all',
  activeJobsMin: '',
  activeJobsMax: '',
  completedJobsMin: '',
  completedJobsMax: '',
  page: '1',
  limit: '25',
  sortColumn: 'firstName',
  sortOrder: 'ASC',
};

type FilterDefaults = typeof FILTER_DEFAULTS;

/**
 * Build the API query from URL filter state.
 * Maps the UI filter shape to the backend ListUsersQuery contract.
 */
function buildApiQuery(filters: FilterDefaults): ListUsersQuery {
  const query: ListUsersQuery = {};
  const page = Math.max(1, parseInt(filters.page, 10) || 1);
  const limit = parseInt(filters.limit, 10) || 25;

  query.limit = String(limit);
  query.skip = String((page - 1) * limit);
  query.sortColumn = filters.sortColumn;
  query.sortOrder = filters.sortOrder as 'ASC' | 'DESC';

  if (filters.search) query.search = filters.search;
  if (filters.name) query.name = filters.name;
  if (filters.email) query.email = filters.email;

  // Status is derived from the tab only — the filters modal no longer carries it.
  if (filters.tab === 'normal') {
    query.status = 'active';
    query.isMobileUser = 'false';
  } else if (filters.tab === 'mobile') {
    query.status = 'active';
    query.isMobileUser = 'true';
  } else if (filters.tab === 'inactive') {
    query.status = 'inactive';
  }

  if (filters.jobTitle !== 'all') query.jobTitle = filters.jobTitle;
  if (filters.team !== 'all') query.team = filters.team;
  if (filters.activeJobsMin) query.activeJobsMin = filters.activeJobsMin;
  if (filters.activeJobsMax) query.activeJobsMax = filters.activeJobsMax;
  if (filters.completedJobsMin) query.completedJobsMin = filters.completedJobsMin;
  if (filters.completedJobsMax) query.completedJobsMax = filters.completedJobsMax;

  return query;
}

export default function UsersPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(FILTER_DEFAULTS);

  const apiQuery = useMemo(() => buildApiQuery(filters as FilterDefaults), [filters]);
  const { data, isLoading, isError } = useUsers(apiQuery);

  const users: User[] = data?.users?.map(userListItemToView) ?? [];
  const totalCount = data?.count ?? 0;
  const page = Math.max(1, parseInt(filters.page, 10) || 1);
  const limit = parseInt(filters.limit, 10) || 25;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  return (
    <UsersView
      users={users}
      isLoading={isLoading}
      isError={isError}
      totalCount={totalCount}
      // Filter state
      activeTab={filters.tab}
      search={filters.search}
      nameFilter={filters.name}
      emailFilter={filters.email}
      jobTitleFilter={filters.jobTitle}
      teamFilter={filters.team}
      activeJobsMin={filters.activeJobsMin}
      activeJobsMax={filters.activeJobsMax}
      completedJobsMin={filters.completedJobsMin}
      completedJobsMax={filters.completedJobsMax}
      // Pagination
      page={page}
      totalPages={totalPages}
      limit={limit}
      // Callbacks
      onTabChange={(tab) => setFilters({ tab, page: '1' })}
      onSearchChange={(search) => setFilters({ search, page: '1' })}
      onFiltersApply={(applied) => setFilters({ ...applied, page: '1' })}
      onFiltersClear={() =>
        setFilters({
          name: '',
          email: '',
          jobTitle: 'all',
          team: 'all',
          search: '',
          activeJobsMin: '',
          activeJobsMax: '',
          completedJobsMin: '',
          completedJobsMax: '',
        })
      }
      onPageChange={(p) => setFilters({ page: String(p) })}
      onUserClick={(userId) => navigate(`/users/${userId}`)}
      onAddUser={() => navigate('/users/add')}
    />
  );
}
